import { CPLOrder } from "../../../../../../api/staff";

/**
 * Преобразует источник в читаемое название платформы
 */
export const transformPlatform = (source: string): string => {
  switch (source) {
    case 'only_fans':
      return 'OnlyFans';
    case 'instagram':
      return 'Instagram';
    case 'twitter':
      return 'Twitter';
    case 'google':
      return 'Google';
    case 'reddit':
      return 'Reddit';
    case 'tiktok':
      return 'TikTok';
    case 'telegram':
      return 'Telegram';
    case 'youtube':
      return 'YouTube';
    case 'another':
      return 'Other';
    default:
      return 'Other';
  }
};

export interface OrderData {
  id: string;
  internalId: string;
  account: {
    id: string;
    of_id?: number;
    username: string;
    avatar?: string;
    platform: string;
    platformIcon: React.ReactNode;
    overall_popularity_percent?: number;
  };
  offer: {
    id: string;
    name: string;
    avatar?: string;
    platform: string;
    subscribers: number;
    pricePerSubscriber: number;
    description?: string;
  };
  campaign: {
    link?: string;
    linkSuffix?: string;
    transitions_count: number;
    transitions_percentage: string;
    statistics: {
      totalUsers: number;
      verifiedUsers: number;
      messages: number | null;
      transactions: {
        total: number;
        count: number;
        breakdown: Record<string, number>;
      };
      cost: number;
      arpu: number;
      arpuUsd: number;
      romi: number;
    };
  };
  status: 'in_progress' | 'pending' | 'completed' | 'not_started' | 'canceled';
  startDate: string;
}

export const transformCPLOrderToOrderData = (cplOrder: CPLOrder, getPlatformIcon: (source: string) => React.ReactNode): OrderData => {
  const transactionStats = cplOrder.transactions_statistic || {};
  const transactionsTotal = Number((transactionStats as any)?.total ?? 0) || 0;

  const transactionsBreakdown = Object.entries(transactionStats).reduce((acc, [key, value]) => {
    if (key === 'total') return acc;
    const numericValue = typeof value === 'number' ? value : Number(value ?? 0);
    acc[key] = Number.isFinite(numericValue) ? numericValue : 0;
    return acc;
  }, {} as Record<string, number>);

  const breakdownValues = Object.values(transactionsBreakdown);
  const purchasesCountFromBreakdown = breakdownValues.length
    ? breakdownValues.reduce((acc, value) => acc + value, 0)
    : 0;

  const totalSubscribers = Number(cplOrder.subs?.count) || 0;
  const verifiedUsers = Number(cplOrder.subscribed_count) || 0;
  const transitionsCount = Number(cplOrder?.model?.transitions_count) || 0;
  //@ts-ignore
  const purchasesCount = cplOrder?.transactions_statistic?.count || 0;

  const cost = Number(cplOrder.total_price) || 0;
  const arpu = totalSubscribers > 0 ? parseFloat((cost / totalSubscribers).toFixed(2)) : 0;
  const arpuUsd = parseFloat((arpu * 0.9).toFixed(2));
  const revenue = transactionsTotal;
  const romi = cost > 0 ? parseFloat((((revenue - cost) / cost) * 100).toFixed(2)) : 0;

  const transitionsPercentage = transitionsCount > 0 && verifiedUsers > 0
    ? `${((verifiedUsers / transitionsCount) * 100).toFixed(2)}%`
    : '0%';

  const transformStatus = (status: string): 'in_progress' | 'pending' | 'completed' | 'not_started' | 'canceled' => {
    switch (status) {
      case 'in_progress': return 'in_progress';
      case 'waiting': return 'pending';
      case 'completed': return 'completed';
      case 'canceled': return 'canceled';
      default: return 'pending';
    }
  };

  return {
    ...cplOrder,
    id: cplOrder.id || cplOrder._id,
    internalId: (cplOrder.id || cplOrder._id).slice(-4),
    account: {
      id: cplOrder.model?.id || '',
      of_id: cplOrder.model?.of_user_id,
      username: cplOrder.model?.of_user_name || '',
      avatar: cplOrder.model?.avatar,
      platform: transformPlatform(cplOrder.offer?.source || ''),
      platformIcon: getPlatformIcon(cplOrder.offer?.source || ''),
      overall_popularity_percent: cplOrder.model?.overall_popularity_percent || 0,
    },
    offer: {
      id: cplOrder.offer?.id || '',
      name: cplOrder.offer?.partner?.name || '',
      avatar: cplOrder.offer?.partner?.avatar,
      platform: transformPlatform(cplOrder.offer?.source || ''),
      subscribers: totalSubscribers,
      pricePerSubscriber: parseFloat(cplOrder.subs?.price || '0') || 0,
      description: '',
    },
    campaign: {
      link: cplOrder.model?.link,
      linkSuffix: cplOrder.model?.link ? cplOrder.model.link.split('/').filter(Boolean).slice(-1)[0] || '' : '',
      transitions_count: transitionsCount,
      transitions_percentage: transitionsPercentage,
      statistics: {
        totalUsers: totalSubscribers,
        verifiedUsers: verifiedUsers,
        messages: cplOrder.subs?.with_gte_3_chat_messages_count ?? null,
        transactions: {
          total: transactionsTotal,
          count: purchasesCount,
          breakdown: transactionsBreakdown,
        },
        cost: cost,
        arpu: arpu,
        arpuUsd: arpuUsd,
        romi: romi,
      },
    },
    status: transformStatus(cplOrder.status),
    startDate: cplOrder.created_at,
  };
};

export const getStatusLabel = (status: string, t?: (key: string) => string): string => {
  if (!t) {
    const STATUS_LABELS: Record<string, string> = {
      completed: 'Завершён',
      processing: 'Обработка',
      pending: 'В ожидании',
      canceled: 'Отменен',
      refunded: 'Возврат',
      in_progress: 'В работе',
      not_started: 'Не начата',
    };
    return STATUS_LABELS[status] || status;
  }

  const statusLabels: Record<string, string> = {
    completed: t('orders.status.completed'),
    processing: t('orders.status.processing'),
    pending: t('orders.status.pending'),
    canceled: t('orders.status.cancelled'),
    refunded: t('orders.status.refunded'),
    in_progress: t('orders.status.inProgress'),
    not_started: t('orders.status.notStarted'),
  };
  return statusLabels[status] || status;
};
